import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TreePalm, Plus, Trash2, Save, Loader2, Wallet, AlertCircle, Calendar, ShieldCheck, FileSpreadsheet } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAVE_TYPES = ['CL', 'SL', 'EL', 'PL', 'ML', 'CompOff', 'LOP', 'Other'];
const ACCRUAL_METHODS = [
  { code: 'monthly', label: 'Monthly Accrual' },
  { code: 'yearly', label: 'Yearly Accrual' },
  { code: 'quarterly', label: 'Quarterly Accrual' },
  { code: 'front_load', label: 'Front-load (Beginning of Period)' },
];
const LOP_RULES = [
  { code: 'auto_lop_when_zero', label: 'Auto LOP when balance is 0' },
  { code: 'convert_to_lop', label: 'Convert excess leave to LOP' },
  { code: 'manual', label: 'Manual HR Review' },
];

const empty = {
  company_id: '', leave_type: 'CL', label: '', is_active: true,
  monthly_eligibility: 1, annual_eligibility: 12, accrual_method: 'monthly',
  carry_forward: false, max_carry_forward: 0,
  encashment_allowed: false, negative_balance_allowed: false, auto_lop_when_zero: true,
  lop_conversion_rule: 'auto_lop_when_zero',
  weekend_treatment: 'exclude', holiday_treatment: 'exclude',
  half_day_allowed: true, late_mark_rule: '', grace_minutes: 0,
  effective_from: '', effective_to: '',
  applicability_categories: [], applicability_departments: [], applicability_locations: [], payroll_groups: [],
  notes: '',
};

export default function LeavePolicy() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        const cs = Array.isArray(data) ? data : (data.companies || []);
        setCompanies(cs);
        if (cs.length === 1) setCompanyId(cs[0].id);
      } catch { /* */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/leave-policy/policies${companyId ? `?company_id=${companyId}` : ''}&include_inactive=true`;
      const { data } = await axios.get(url, { withCredentials: true });
      setPolicies(data.policies || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load leave policies');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [companyId, load]);

  const save = async (p) => {
    try {
      const { data } = await axios.post(`${API_URL}/leave-policy/policies`, p, { withCredentials: true });
      if (data.action === 'version_created') {
        toast.success('Policy updated: New Version created to preserve historical data');
      } else {
        toast.success('Policy saved successfully');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Deactivate/Delete leave policy ${p.leave_type} (v${p.version || 1})?`)) return;
    try {
      const { data } = await axios.delete(`${API_URL}/leave-policy/policies/${p.id}`, { withCredentials: true });
      if (data.action === 'soft_deleted') {
        toast.success('Policy soft-deleted (marked inactive) to preserve historical transactions');
      } else {
        toast.success('Policy deleted');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="leave-policy-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <TreePalm className="text-emerald-600" size={28} /> Leave Policy Master
          </h1>
          <p className="text-sm text-slate-500 mt-1">Configure company-wide leave entitlements, accruals, versioning, and policy controls.</p>
        </div>
        <a href="/leave-policy/balances" className="inline-flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-semibold px-4 py-2 rounded-lg text-sm border border-emerald-200">
          <Wallet size={16} /> View & Manage Leave Balances →
        </a>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex items-center gap-3 shadow-sm">
        <select
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          data-testid="policy-company"
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-medium bg-white"
        >
          <option value="">— Select Company —</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button
          onClick={() => setEditing({ ...empty, company_id: companyId })}
          disabled={!companyId}
          data-testid="policy-add"
          className="ml-auto px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-1.5 shadow-sm"
        >
          <Plus size={16} /> Add Leave Policy
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="animate-spin inline text-slate-400" /></div>
        ) : policies.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No policies for this company. Select a company and click <b>Add Leave Policy</b>.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-600 text-xs font-semibold uppercase border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Version</th>
                  <th className="px-4 py-3">Leave Type</th>
                  <th className="px-4 py-3 text-right">Monthly</th>
                  <th className="px-4 py-3 text-right">Annual</th>
                  <th className="px-4 py-3">Accrual Method</th>
                  <th className="px-4 py-3 text-center">Carry Forward</th>
                  <th className="px-4 py-3 text-center">Encashment</th>
                  <th className="px-4 py-3 text-center">Negative Bal.</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {policies.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50/80 transition-colors" data-testid={`policy-row-${p.leave_type}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500 font-bold">v{p.version || 1}</td>
                    <td className="px-4 py-3 font-bold text-indigo-700">
                      {p.leave_type} {p.label ? <span className="ml-2 text-slate-500 font-normal">— {p.label}</span> : null}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-medium">{p.monthly_eligibility}</td>
                    <td className="px-4 py-3 text-right font-mono font-medium">{p.annual_eligibility}</td>
                    <td className="px-4 py-3 text-xs capitalize text-slate-600 font-medium">{p.accrual_method || 'monthly'}</td>
                    <td className="px-4 py-3 text-center">
                      {p.carry_forward ? (
                        <span className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-2 py-0.5 rounded-full border border-emerald-200">
                          Yes ({p.max_carry_forward} max)
                        </span>
                      ) : <span className="text-slate-400">No</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.encashment_allowed ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {p.negative_balance_allowed ? <span className="text-amber-600 font-bold">✓</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.is_active ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500'}`}>
                        {p.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setEditing(p)} className="text-indigo-600 hover:underline text-xs font-semibold mr-3">Edit</button>
                      {p.is_active && (
                        <button onClick={() => remove(p)} className="text-rose-600 hover:underline text-xs font-semibold">
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <PolicyEditor policy={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function PolicyEditor({ policy, onClose, onSave }) {
  const [p, setP] = useState(policy);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-slate-200"
        onClick={(e) => e.stopPropagation()} data-testid="policy-editor">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h2 className="font-bold text-slate-900 text-base">
            {p.id ? `Edit Leave Policy (Version v${p.version || 1})` : 'Configure New Leave Policy'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-sm">✕</button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4 text-sm overflow-y-auto flex-1">
          <Field label="Leave Type *">
            <select value={p.leave_type} onChange={(e) => set('leave_type', e.target.value)}
              data-testid="policy-leave-type" className="input font-bold text-indigo-700">
              {LEAVE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Display Label">
            <input value={p.label || ''} onChange={(e) => set('label', e.target.value)} className="input" placeholder="e.g. Casual Leave" />
          </Field>

          <Field label="Accrual Method">
            <select value={p.accrual_method} onChange={(e) => set('accrual_method', e.target.value)} className="input font-medium">
              {ACCRUAL_METHODS.map((m) => <option key={m.code} value={m.code}>{m.label}</option>)}
            </select>
          </Field>

          <Field label="LOP Conversion Rule">
            <select value={p.lop_conversion_rule} onChange={(e) => set('lop_conversion_rule', e.target.value)} className="input font-medium">
              {LOP_RULES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </Field>

          <Field label="Monthly Eligibility (days)">
            <input type="number" step="0.25" value={p.monthly_eligibility}
              onChange={(e) => set('monthly_eligibility', parseFloat(e.target.value || 0))} className="input font-mono" />
          </Field>
          <Field label="Annual Entitlement (days)">
            <input type="number" step="0.25" value={p.annual_eligibility}
              onChange={(e) => set('annual_eligibility', parseFloat(e.target.value || 0))} className="input font-mono" />
          </Field>

          <Field label="Max Carry Forward Limit">
            <input type="number" step="0.5" value={p.max_carry_forward || 0}
              onChange={(e) => set('max_carry_forward', parseFloat(e.target.value || 0))} className="input font-mono" />
          </Field>

          <Field label="Effective From Date">
            <input type="date" value={p.effective_from || ''} onChange={(e) => set('effective_from', e.target.value)} className="input" />
          </Field>

          <Field label="Effective To Date">
            <input type="date" value={p.effective_to || ''} onChange={(e) => set('effective_to', e.target.value)} className="input" />
          </Field>

          <Field label="Weekend Treatment">
            <select value={p.weekend_treatment} onChange={(e) => set('weekend_treatment', e.target.value)} className="input">
              <option value="exclude">Exclude Weekends</option>
              <option value="include">Include Weekends (Sandwich)</option>
            </select>
          </Field>

          <Field label="Holiday Treatment">
            <select value={p.holiday_treatment} onChange={(e) => set('holiday_treatment', e.target.value)} className="input">
              <option value="exclude">Exclude Holidays</option>
              <option value="include">Include Holidays (Sandwich)</option>
            </select>
          </Field>

          <Field label="Applicable Employee Categories">
            <input value={(p.applicability_categories || []).join(', ')}
              onChange={(e) => set('applicability_categories', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
              className="input" placeholder="Regular, Trainee, Executive" />
          </Field>
          <Field label="Applicable Departments">
            <input value={(p.applicability_departments || []).join(', ')}
              onChange={(e) => set('applicability_departments', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
              className="input" placeholder="Engineering, Sales, HR" />
          </Field>
          <Field label="Applicable Locations / Branches">
            <input value={(p.applicability_locations || []).join(', ')}
              onChange={(e) => set('applicability_locations', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
              className="input" placeholder="Mumbai, Bangalore, HQ" />
          </Field>

          <div className="col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 border-t border-slate-100 bg-slate-50 p-3 rounded-xl">
            {[
              { k: 'is_active', l: 'Active Status' },
              { k: 'carry_forward', l: 'Carry Forward Allowed' },
              { k: 'encashment_allowed', l: 'Encashment Allowed' },
              { k: 'half_day_allowed', l: 'Half Day Allowed' },
              { k: 'negative_balance_allowed', l: 'Negative Balance Allowed' },
              { k: 'auto_lop_when_zero', l: 'Auto LOP when Balance = 0' },
            ].map(({ k, l }) => (
              <label key={k} className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                <input type="checkbox" checked={!!p[k]} onChange={(e) => set(k, e.target.checked)} className="rounded text-indigo-600" />
                {l}
              </label>
            ))}
          </div>

          <Field label="Policy Remarks / Notes" colSpan>
            <textarea rows={2} value={p.notes || ''} onChange={(e) => set('notes', e.target.value)} className="input" placeholder="Additional policy controls or guidelines..." />
          </Field>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-slate-700">Cancel</button>
          <button onClick={() => onSave(p)}
            data-testid="policy-save"
            className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold flex items-center gap-1.5 shadow-sm">
            <Save size={16} /> Save Policy Version
          </button>
        </div>
      </div>
      <style>{`
        .input { width: 100%; border: 1px solid #cbd5e1; border-radius: 0.5rem; padding: 0.5rem 0.75rem; font-size: 0.875rem; }
      `}</style>
    </div>
  );
}

function Field({ label, children, colSpan }) {
  return (
    <div className={colSpan ? 'col-span-2' : ''}>
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
